import { Table } from 'components-antd';
import styles from './styles.module.scss';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { convertNameToAvatarPlaceholder } from 'helpers';
import { Avatar } from 'components';
import PortfolioItemActionsMenu from '../PortfolioItemActionsMenu';
import { Icons } from 'pages/Radar/Icons';
import { openNewTab } from 'services/newTab';
import { useHistory } from 'react-router-dom';

const PropertiesTable = ({ properties, searchQuery, handlePortfolioItemArchive }) => {
  const history = useHistory();
  const [internalProperties, setInternalProperties] = useState<any[]>([]);

  useEffect(() => {
    filterProperties();
  }, [searchQuery, properties]);

  const filterProperties = () => {
    if (!searchQuery) setInternalProperties(properties);
    else handleSearchQuery();
  };

  const handleSearchQuery = () => {
    const trimmedValue = searchQuery.trim();
    if (!trimmedValue) {
      setInternalProperties(properties);
      return;
    }

    const filteredProperties = properties.filter((property) =>
      property.itemsStringified?.toLowerCase()?.includes(trimmedValue.toLowerCase()),
    );
    setInternalProperties(filteredProperties);
  };

  const handleRowClick = (record, e) => {
    const { addressId, property, clientId, clientName } = record;
    const searchParams = new URLSearchParams({
      address1: property?.line1,
      address2: property?.line2,
      addressId,
      clientName,
      clientId,
    }).toString();
    const path = `/radar/property?${searchParams}`;
    if (e.ctrlKey || e.metaKey) {
      openNewTab(path);
    } else {
      history.push(path);
    }
  };

  const columns: any[] = [
    {
      title: 'Property',
      key: 'propertyAddress',
      dataIndex: 'propertyAddress',
      fixed: 'left',
      sorter: (a, b) => {
        if (a.propertyAddress < b.propertyAddress) return -1;
        if (a.propertyAddress > b.propertyAddress) return 1;
        return 0;
      },
      width: 300,
      render: (_, row) => {
        return (
          <div className={styles.property}>
            {row.propertyPhotoUrl ? (
              <Avatar
                className={styles.propertyAvatarWrap}
                avatarClassName={styles.propertyAvatar}
                src={row.propertyPhotoUrl}
                placeholder={convertNameToAvatarPlaceholder(row.propertyAddress)}
              />
            ) : (
              <div className={styles.propertyIcon}>
                <Icons variant={Icons.CONDO} />
              </div>
            )}

            <div className={styles.right}>
              <div className={styles.address}>{row.propertyAddress}</div>
              <div className={styles.locations}>{row.locationAddress}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Client',
      key: 'clientName',
      dataIndex: 'clientName',
      sorter: (a, b) => {
        if (a.clientName < b.clientName) return -1;
        if (a.clientName > b.clientName) return 1;
        return 0;
      },
      width: 250,
      render: (_, row) => {
        return (
          <div className={styles.client}>
            <Avatar
              className={styles.avatarWrap}
              avatarClassName={styles.avatar}
              src={row.clientAvatarUrl}
              placeholder={convertNameToAvatarPlaceholder(
                `${row.clientFirstName}${row.clientLastName ? ` ${row.clientLastName}` : ''}`,
              )}
            />
            <div className={classNames(styles.text, styles.name)}>{row.clientName}</div>
          </div>
        );
      },
    },
    {
      title: 'Est. Value',
      key: 'homeValue',
      dataIndex: 'homeValue',
      sorter: (a, b) => {
        if (a.homeValue < b.homeValue) return -1;
        if (a.homeValue > b.homeValue) return 1;
        return 0;
      },
      width: 200,
      render: (_, row) => {
        return <div className={styles.text}>{row.homeValue}</div>;
      },
    },
    {
      title: 'Est. Rental Value',
      key: 'rentalValue',
      dataIndex: 'rentalValue',
      sorter: (a, b) => {
        if (a.rentalValue < b.rentalValue) return -1;
        if (a.rentalValue > b.rentalValue) return 1;
        return 0;
      },
      width: 250,
      render: (_, row) => {
        return <div className={styles.text}>{row.rentalValue}</div>;
      },
    },
    {
      title: 'Neighborhood Activity',
      key: 'activity',
      dataIndex: 'activity',
      sorter: (a, b) => {
        if (a.activity < b.activity) return -1;
        if (a.activity > b.activity) return 1;
        return 0;
      },
      width: 250,
      render: (_, row) => {
        return <div className={styles.text}>{row.activity}</div>;
      },
    },
    {
      title: 'Closed',
      key: 'closed',
      dataIndex: 'closed',
      sorter: (a, b) => {
        if (a.closed < b.closed) return -1;
        if (a.closed > b.closed) return 1;
        return 0;
      },
      width: 150,
      render: (_, row) => {
        return <div className={styles.text}>{row.closed}</div>;
      },
    },
    {
      title: '',
      key: '',
      dataIndex: '',
      width: 75,
      render: (_, row) => {
        return (
          <PortfolioItemActionsMenu
            clientId={row.clientId}
            addressId={row.addressId}
            isArchived={!!row.isArchived}
            handlePortfolioItemArchive={handlePortfolioItemArchive}
          />
        );
      },
    },
  ];

  return (
    <Table
      className={styles.propertiesTable}
      bordered={false}
      columns={columns}
      dataSource={internalProperties.map((item, key) => ({
        ...item,
        key,
      }))}
      pagination={false}
      scroll={{ x: 'max-content' }}
      onRow={(record) => ({
        onClick: (e) => handleRowClick(record, e),
      })}
    />
  );
};

export default PropertiesTable;
